import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { MantineProvider } from "@mantine/core";
import { useOutlet } from "react-router-dom";

import { AppPage } from "../../components/AppPage";
import { PaginationV2 } from "../../components/PaginationV2";
import { RpiPagination, RpiPaginationRow } from "../../components/RpiPagination";
import { RpiTableV2Container, RpiTableFooter } from "../../components/RpiTableV2Container";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { printAssetColumns } from "../../configuration/columns/printAsset.column";
import { usePrintAssetFilters } from "../../configuration/filters/printAsset.filter";
import { SearchInput, toSearchInputArray } from "../../configuration/filters/searchInput";
import { featureFlags } from "../../featureFlags";
import { useDrawerRoute } from "../../hooks/useDrawerRoute";
import { useRpiPagination } from "../../hooks/usePagination";
import { RpiPerPage } from "../../hooks/useRpiPerPage";
import { RpiTable } from "../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../utils/graphql.util";
import { printAssetSearchLabels, printAssetSearchRenderers } from "../../utils/searchField.util";
import { PrintAssetsFilter, path as filterPath } from "./PrintAssetsFilter";

export const PrintAssetsView: React.FC = () => {
  const outlet = useOutlet();
  const filterDrawerRoute = useDrawerRoute(filterPath);

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.PrintAssetsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.PrintAssetSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.PrintAssetsQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (filters: SearchInput<GraphQL.PrintAssetSearchFields>[]) => SearchInput<GraphQL.PrintAssetSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const pagination = useRpiPagination({ perPage: featureFlags.newTableView ? 20 : 10 });

  const printAssetFilters = usePrintAssetFilters();

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error, refetch } = GraphQL.usePrintAssetsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.printAssets.totalCount),
    }
  );
  const printAssets = React.useMemo(() => data?.printAssets.data, [data]);

  return (
    <AppPage path={["Print Assets"]} onReload={refetch}>
      {!filterDrawerRoute.isOpen && outlet}

      <AppPage.Section>
        {featureFlags.newSearchExperience ? (
          <SearchFilterBar
            placeholder="Search by Upload ID"
            isLoading={isLoading}
            filters={searchFilters}
            onSetFilters={setSearchFilters}
            onSearchSubmit={handleSearchSubmit}
            onFilterClick={filterDrawerRoute.open}
            filterLabels={printAssetSearchLabels}
            valueRenderers={printAssetSearchRenderers}
          />
        ) : (
          <RpiSearchFilter
            placeholder="Search by Upload ID"
            filters={printAssetFilters}
            dateRangeField={GraphQL.PrintAssetSearchFields.UpdatedAt}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />
        )}

        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <RpiTableV2Container>
              <RpiTable
                variant="normalV2"
                emptyMessage={
                  searchInputs?.length || searchGeneral
                    ? "No print assets found."
                    : "Your print assets will appear here."
                }
                isLoading={isLoading}
                error={error}
                perPage={printAssets?.length}
                data={printAssets}
                columns={printAssetColumns}
                withSortBy={{ onChange: setSortInput }}
              />
              <RpiTableFooter>
                <PaginationV2 isLoading={isLoading} pagination={pagination} label="Files" />
              </RpiTableFooter>
            </RpiTableV2Container>
          </MantineProvider>
        ) : (
          <>
            <RpiTable
              emptyMessage={
                searchInputs?.length || searchGeneral ? "No print assets found." : "Your print assets will appear here."
              }
              isLoading={isLoading}
              error={error}
              perPage={pagination.perPage}
              data={printAssets}
              columns={printAssetColumns}
              withSortBy={{ onChange: setSortInput }}
            />
            <RpiPaginationRow>
              <RpiPagination pagination={pagination} />
              <RpiPerPage perPage={pagination.perPage} setPerPage={pagination.setPerPage} />
            </RpiPaginationRow>
          </>
        )}
      </AppPage.Section>
      {featureFlags.newSearchExperience && (
        <PrintAssetsFilter
          isOpened={filterDrawerRoute.isOpen}
          onClose={filterDrawerRoute.close}
          filters={searchFilters}
          onApply={setSearchInputs}
        />
      )}
    </AppPage>
  );
};
